import React, { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import CustomSelect from '@/components/UI/Select'
import { Popover } from '@mui/material'
import DateRangePickerComponent from '../UI/DateRangePicker/DateRangePicker'
import dayjs from 'dayjs'

interface DateOption {
  value: string
  label: string
}

interface DateSelectProps {
  onChange: (dateFilter: {
    fromDate: string | null
    toDate: string | null
    dateParameters: DateOption
  }) => void
  initialValue?: DateOption
  width?: string
  value?: {
    fromDate?: string | null
    toDate?: string | null
  }
  allTimeOption?: boolean
}

const DateSelect: React.FC<DateSelectProps> = ({
  onChange,
  initialValue,
  width = '200px',
  allTimeOption = false,
}) => {
  const { t } = useTranslation('translation')
  const currentDate = new Date()

  const dateOptions: DateOption[] = useMemo(
    () => [
      ...(allTimeOption ? [{ value: 'all_time', label: t('All time') }] : []),
      { value: '60', label: t('Last 60 days') },
      { value: '30', label: t('Last 30 days') },
      { value: '7', label: t('Last 7 days') },
      { value: 'custom', label: t('Custom') },
    ],
    [t, allTimeOption],
  )

  const [selected, setSelected] = useState<DateOption>(
    initialValue || {
      value: '30',
      label: t('Last 30 days'),
    },
  )
  const [customValue, setCustomValue] = useState(() => ({
    startDate: currentDate,
    endDate: currentDate,
  }))

  const [showDatePicker, setShowDatePickerValue] = useState<boolean>(false)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const handleClose = () => {
    setAnchorEl(null)
    setShowDatePickerValue(false)
  }

  const id = showDatePicker ? 'date-select-popover' : undefined

  return (
    <>
      <CustomSelect
        options={dateOptions}
        value={selected?.value || ''}
        onClick={(option, el) => {
          if (option?.value === 'custom') {
            setAnchorEl(el)
            setShowDatePickerValue(true)
          }
        }}
        onChange={(_value, option) => {
          if (option?.value === 'custom') return
          setCustomValue({ startDate: undefined, endDate: undefined })
          setSelected(option)

          const isNumberOption = option?.value && !isNaN(Number(option?.value))

          const formattedFromDate = isNumberOption
            ? dayjs()
                .subtract(+option.value || 30, 'day')
                .format('YYYY-MM-DD')
            : undefined
          const formattedToDate = isNumberOption
            ? dayjs().format('YYYY-MM-DD')
            : undefined

          onChange({
            fromDate: formattedFromDate,
            toDate: formattedToDate,
            dateParameters: option,
          })
        }}
        slotProps={{ root: { sx: { width } } }}
      />
      <Popover
        id={id}
        open={showDatePicker}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        <DateRangePickerComponent
          unUseDefaultClass
          dateValue={customValue}
          onDateChange={dateRange => {
            const filter = {
              fromDate: dateRange ? dateRange.startDate : null,
              toDate: dateRange ? dateRange.endDate : null,
              dateParameters: { label: t('Custom'), value: 'custom' },
            }

            setSelected({ label: t('Custom'), value: 'custom' })
            setCustomValue({
              startDate: filter?.fromDate
                ? new Date(filter?.fromDate)
                : currentDate,
              endDate: filter?.toDate ? new Date(filter?.toDate) : currentDate,
            })

            onChange(filter)
          }}
          onCloseDateRangeHandler={() => setShowDatePickerValue(false)}
        />
      </Popover>
    </>
  )
}

export default DateSelect
